import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Building2, LayoutDashboard, Library, ListChecks, Radio, Users } from 'lucide-react'

/** The admin surfaces; the active one is highlighted in the sub-nav. */
export type AdminSection =
  | 'overview'
  | 'users'
  | 'organizations'
  | 'sessions'
  | 'quizzes'
  | 'questionBank'

type AdminNavItem = {
  section: AdminSection
  to:
    | '/admin'
    | '/admin/users'
    | '/admin/organizations'
    | '/admin/sessions'
    | '/admin/quizzes'
    | '/admin/question-bank'
  labelKey:
    | 'navOverview'
    | 'navUsers'
    | 'navOrganizations'
    | 'navSessions'
    | 'navQuizzes'
    | 'navQuestionBank'
  icon: typeof LayoutDashboard
}

const NAV_ITEMS: AdminNavItem[] = [
  { section: 'overview', to: '/admin', labelKey: 'navOverview', icon: LayoutDashboard },
  { section: 'users', to: '/admin/users', labelKey: 'navUsers', icon: Users },
  { section: 'organizations', to: '/admin/organizations', labelKey: 'navOrganizations', icon: Building2 },
  { section: 'sessions', to: '/admin/sessions', labelKey: 'navSessions', icon: Radio },
  { section: 'quizzes', to: '/admin/quizzes', labelKey: 'navQuizzes', icon: ListChecks },
  { section: 'questionBank', to: '/admin/question-bank', labelKey: 'navQuestionBank', icon: Library },
]

type AdminShellProps = {
  /** Which sub-nav entry is current (drives the active pill). */
  active: AdminSection
  /** Page heading shown under the brand rule. */
  title: string
  /** Optional trailing controls rendered beside the heading (e.g. a CTA). */
  actions?: React.ReactNode
  children: React.ReactNode
}

/**
 * Shared chrome for every admin page: the dashboard gradient backdrop, the
 * brand rule + page heading, and the admin sub-nav pill row. Each admin route
 * calls {@link useRequireAdmin} itself (the documented fallback to a TanStack
 * directory-layout route) and wraps its body in this shell so the four
 * surfaces share identical orientation and styling.
 */
export function AdminShell({ active, title, actions, children }: AdminShellProps) {
  const { t } = useTranslation('admin')

  return (
    <main className="min-h-screen bg-gradient-to-bl from-brand-50/40 via-white to-white">
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 py-12">
        <header className="mb-8">
          <div
            className="inline-block h-1 w-12 rounded-full bg-brand-500 mb-4"
            aria-hidden="true"
          />
          <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
            <h1 className="text-4xl sm:text-5xl font-bold font-display text-brand-900 tracking-tight">
              {title}
            </h1>
            {actions ? (
              <div className="flex flex-wrap items-center gap-3">{actions}</div>
            ) : null}
          </div>

          {/* Sub-nav pill row — scrolls horizontally on narrow screens. */}
          <nav aria-label={t('adminDashboard')} className="mt-6 -mx-1 overflow-x-auto">
            <ul className="flex items-center gap-2 px-1">
              {NAV_ITEMS.map(({ section, to, labelKey, icon: Icon }) => {
                const isActive = section === active
                return (
                  <li key={section}>
                    <Link
                      to={to}
                      aria-current={isActive ? 'page' : undefined}
                      className={
                        'inline-flex items-center gap-2 whitespace-nowrap rounded-full px-3.5 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 ' +
                        (isActive
                          ? 'bg-brand-500 text-white shadow-sm hover:bg-brand-600'
                          : 'text-brand-700 hover:bg-brand-50 hover:text-brand-900')
                      }
                    >
                      <Icon className="h-4 w-4" aria-hidden="true" />
                      <span>{t(labelKey)}</span>
                    </Link>
                  </li>
                )
              })}
            </ul>
          </nav>
        </header>

        {children}
      </div>
    </main>
  )
}
